import React from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import { formatDecimalToPercentage, formatPrice } from '~/util/format';
import { getLinearProgressValue } from '~/util/convertFunctions';
import PanelBodyText from './PanelBodyText';
import QuotaPercentageContainer from './QuotaPercentageContainer';
import * as Style from './styles';

// O modo (quota/superQuota/hyperQuota) é determinado pelo MainPanelCard e passado via dayMode.

function DayModeBody({
    dayMode,
    salesNumber,
    dailyQuotaValue,
    superQuotaMultiplier,
    hyperQuotaMultiplier,
    showRecalculatedQuota,
    recalculatedQuota,
    salesPercentage,
    secondaryProgressView,
}) {
    const baseQuota =
        showRecalculatedQuota && recalculatedQuota
            ? recalculatedQuota
            : dailyQuotaValue;
    const isRecalculated = showRecalculatedQuota && recalculatedQuota;

    const modeConfig = {
        quota: {
            title: isRecalculated ? 'Cota recalculada do dia' : 'Cota do dia',
            quotaValue: isRecalculated ? recalculatedQuota : dailyQuotaValue,
            percentage: isRecalculated
                ? formatDecimalToPercentage(
                      salesNumber / recalculatedQuota / 100
                  )
                : salesPercentage,
        },
        superQuota: {
            title: isRecalculated
                ? 'Supercota recalculada do dia'
                : 'Supercota do dia',
            quotaValue: baseQuota * superQuotaMultiplier,
            percentage: formatDecimalToPercentage(
                salesNumber / (baseQuota * superQuotaMultiplier) / 100
            ),
        },
        hyperQuota: {
            title: isRecalculated
                ? 'Hipercota recalculada do dia'
                : 'Hipercota do dia',
            quotaValue: baseQuota * hyperQuotaMultiplier,
            percentage: formatDecimalToPercentage(
                salesNumber / (baseQuota * hyperQuotaMultiplier) / 100
            ),
        },
    };

    const { title, quotaValue, percentage } = modeConfig[dayMode] || {};
    if (!title) return null;

    const progressValue = quotaValue
        ? getLinearProgressValue(salesNumber / quotaValue)
        : 0;

    return (
        <Style.MainPanelComponentContainer
            primary={dayMode === 'quota' && !secondaryProgressView}
        >
            <Style.InnerMainPanelComponent mr={2}>
                <PanelBodyText
                    text={title}
                    value={
                        quotaValue ? formatPrice(quotaValue) : 'Indisponível'
                    }
                />
                <Style.StyledLinearProgress
                    variant="determinate"
                    value={progressValue}
                    quota={quotaValue}
                />
            </Style.InnerMainPanelComponent>
            <QuotaPercentageContainer value={percentage} />
        </Style.MainPanelComponentContainer>
    );
}

function MonthModeBody({
    quota,
    salesNumber,
    hasAchievedMonthlySuperQuota,
    shouldShowCongratulationsMessage,
    showRecalculatedQuota,
    salesPercentage,
    isLastDay,
    secondaryProgressView,
    hideSuperQuota,
    isTodayBeforeFinalDate,
}) {
    const { finalDate, period } = useSelector(state => state.status);

    const monthlyQuota = quota?.periodQuotas?.find(p => p.period === 'month');

    const isLastDayOfPeriod =
        period === 'month' && isLastDay && !isTodayBeforeFinalDate;

    // Barra de progresso parcial: exibe quota.totalQuota (= quotaUntilEndDate, cota acumulada até hoje)
    const incompleteMonthlyQuotaProgress =
        !showRecalculatedQuota &&
        !isLastDayOfPeriod &&
        !shouldShowCongratulationsMessage &&
        !secondaryProgressView;

    // NV: exibe barra simples de cota quando já atingiu a cota mas hideSuperQuota está ativo
    const NVCompletedQuotaCondition =
        shouldShowCongratulationsMessage &&
        period !== 'month' &&
        hideSuperQuota;

    const canApplyHyperQuota = !!(
        hasAchievedMonthlySuperQuota && monthlyQuota?.hiperQuotaMultiplier
    );

    const getDateTitle = () => {
        switch (period) {
            case 'month': {
                const displayDate = isTodayBeforeFinalDate
                    ? moment()
                    : moment(finalDate);
                return `Cota até ${displayDate.format('D MMM')}`;
            }
            case 'week':
                return 'Cota da semana';
            case 'custom':
                return 'Cota';
            default:
                return `Cota até ${moment(finalDate).format('D MMM')}`;
        }
    };

    const getTitle = () => {
        if (
            period === 'month' &&
            shouldShowCongratulationsMessage &&
            canApplyHyperQuota
        )
            return 'Hipercota';
        if (period === 'month' && shouldShowCongratulationsMessage)
            return 'Supercota';
        if (incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition)
            return getDateTitle();
        if (period === 'month') return 'Cota do mês';
        return getDateTitle();
    };

    const isQuotaAvailable = () => {
        if (incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition)
            return !!quota?.totalQuota;
        if (period === 'month') return !!monthlyQuota?.quota;
        if (shouldShowCongratulationsMessage)
            return !!quota?.totalQuotaAsNumber;
    };

    const getQuotaValue = () => {
        if (incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition)
            return quota?.totalQuota;
        if (
            period === 'month' &&
            shouldShowCongratulationsMessage &&
            canApplyHyperQuota
        )
            return formatPrice(
                monthlyQuota.quota * monthlyQuota.hiperQuotaMultiplier
            );
        if (period === 'month' && shouldShowCongratulationsMessage)
            return formatPrice(
                monthlyQuota.quota * monthlyQuota.superQuotaMultiplier
            );
        if (period === 'month') return formatPrice(monthlyQuota?.quota);
        return formatPrice(
            quota?.totalQuotaAsNumber * monthlyQuota?.superQuotaMultiplier
        );
    };

    const getLinearProgressQuota = () => {
        if (
            period === 'month' &&
            shouldShowCongratulationsMessage &&
            canApplyHyperQuota
        )
            return monthlyQuota.quota * monthlyQuota.hiperQuotaMultiplier;
        if (period === 'month' && shouldShowCongratulationsMessage)
            return monthlyQuota.quota * monthlyQuota.superQuotaMultiplier;
        if (period !== 'month' && shouldShowCongratulationsMessage)
            return (
                quota?.totalQuotaAsNumber * monthlyQuota?.superQuotaMultiplier
            );
        if (incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition)
            return quota?.totalQuota || 0;
        if (period === 'month') return monthlyQuota?.quota;
        return quota?.totalQuotaAsNumber;
    };

    const linearProgressQuota = getLinearProgressQuota();

    // Barra incompleta usa salesPercentage diretamente (já calculado no pai como vendas/quotaUntilEndDate)
    const progressRatio =
        incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition
            ? salesPercentage
            : salesNumber / linearProgressQuota;

    const linearProgressValue = getLinearProgressValue(progressRatio);
    const percentageText =
        incompleteMonthlyQuotaProgress || NVCompletedQuotaCondition
            ? salesPercentage
            : formatDecimalToPercentage(progressRatio / 100);

    return (
        <Style.MainPanelComponentContainer primary={!secondaryProgressView}>
            <Style.InnerMainPanelComponent mr={2}>
                <PanelBodyText
                    text={getTitle()}
                    value={
                        isQuotaAvailable() ? getQuotaValue() : 'Indisponível'
                    }
                />
                <Style.StyledLinearProgress
                    variant="determinate"
                    value={linearProgressValue}
                    quota={linearProgressQuota}
                />
            </Style.InnerMainPanelComponent>
            <QuotaPercentageContainer value={percentageText} />
        </Style.MainPanelComponentContainer>
    );
}

function PanelBody({ dayMode, ...props }) {
    if (dayMode) return <DayModeBody dayMode={dayMode} {...props} />;
    return <MonthModeBody {...props} />;
}

export default PanelBody;
